import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { formatCrmInteractionDateTime, logCrmContactInteraction } from '@/lib/crmInteractionLog'

export type ContactFollowUpState = {
  id: string
  follow_up_id: string
  status: string
  stop_reason: string | null
  next_fire_at: string | null
  next_fire_reason: string | null
  intent_hold_until: string | null
  intent_hold_reason: string | null
  enrollment_source: string
  manual_anchor_at: string | null
  follow_up: { id: string; name: string } | null
}

export type AvailableFollowUpSequence = {
  id: string
  name: string
  assistant_id: string
  is_active: boolean
}

export type FollowUpMutationResult = { ok: boolean; error?: string }

interface UseContactFollowUpSequencesReturn {
  sequences: ContactFollowUpState[]
  availableSequences: AvailableFollowUpSequence[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  enroll: (followUpId: string) => Promise<FollowUpMutationResult>
  stop: (stateId: string) => Promise<FollowUpMutationResult>
}

/**
 * Espelha mutações entre instâncias do hook — o modal do chat e o painel do lead
 * na tela de Contatos abrem o mesmo contato em componentes separados. O realtime
 * continua ativo, mas não dá para depender só dele para o feedback imediato.
 * Mesmo mecanismo de `useScheduledMessages`.
 */
const changeListeners = new Map<string, Set<() => void>>()

function broadcastChange(contactId: string) {
  changeListeners.get(contactId)?.forEach((listener) => listener())
}

function subscribeToChanges(contactId: string, listener: () => void) {
  const listeners = changeListeners.get(contactId) ?? new Set()
  listeners.add(listener)
  changeListeners.set(contactId, listeners)

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) changeListeners.delete(contactId)
  }
}

/** O PostgREST devolve o join embutido como objeto ou array conforme a cardinalidade. */
type JoinedFollowUp = { id: string; name: string } | { id: string; name: string }[] | null

type FollowUpStateSelectRow = {
  id: string
  follow_up_id: string
  status: string
  stop_reason: string | null
  next_fire_at: string | null
  next_fire_reason: string | null
  intent_hold_until: string | null
  intent_hold_reason: string | null
  enrollment_source: string | null
  manual_anchor_at: string | null
  follow_up: JoinedFollowUp
}

function normalizeJoined(value: JoinedFollowUp): { id: string; name: string } | null {
  return Array.isArray(value) ? value[0] ?? null : value
}

export function useContactFollowUpSequences(
  contactId: string | null | undefined
): UseContactFollowUpSequencesReturn {
  const { user } = useAuth()
  const [sequences, setSequences] = useState<ContactFollowUpState[]>([])
  const [availableSequences, setAvailableSequences] = useState<AvailableFollowUpSequence[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchSequences = useCallback(async () => {
    if (!supabase || !user?.account_id || !contactId) {
      setSequences([])
      setAvailableSequences([])
      setLoading(false)
      return
    }

    const client = supabase
    const accountId = user.account_id

    try {
      setLoading(true)
      setError(null)

      const [statesRes, followUpsRes] = await Promise.all([
        client
          .from('account_contact_follow_up_states')
          .select(
            'id, follow_up_id, status, stop_reason, next_fire_at, next_fire_reason, intent_hold_until, intent_hold_reason, enrollment_source, manual_anchor_at, follow_up:account_assistant_follow_ups(id, name)'
          )
          .eq('account_contact_id', contactId)
          .eq('account_id', accountId)
          .order('updated_at', { ascending: false }),
        client
          .from('account_assistant_follow_ups')
          .select('id, name, assistant_id, is_active')
          .eq('account_id', accountId)
          .eq('is_active', true)
          .order('name', { ascending: true }),
      ])

      if (statesRes.error) throw statesRes.error
      if (followUpsRes.error) throw followUpsRes.error

      setSequences(
        ((statesRes.data ?? []) as unknown as FollowUpStateSelectRow[]).map((row) => ({
          id: row.id,
          follow_up_id: row.follow_up_id,
          status: row.status,
          stop_reason: row.stop_reason ?? null,
          next_fire_at: row.next_fire_at ?? null,
          next_fire_reason: row.next_fire_reason ?? null,
          intent_hold_until: row.intent_hold_until ?? null,
          intent_hold_reason: row.intent_hold_reason ?? null,
          enrollment_source: row.enrollment_source ?? 'auto',
          manual_anchor_at: row.manual_anchor_at ?? null,
          follow_up: normalizeJoined(row.follow_up),
        }))
      )
      setAvailableSequences((followUpsRes.data ?? []) as AvailableFollowUpSequence[])
    } catch (err: any) {
      console.error('[useContactFollowUpSequences] fetch error:', err)
      setError(err?.message ?? 'Erro ao carregar sequências do contato')
    } finally {
      setLoading(false)
    }
  }, [contactId, user?.account_id])

  const enroll = useCallback(
    async (followUpId: string): Promise<FollowUpMutationResult> => {
      if (!supabase || !user?.account_id || !contactId) {
        return { ok: false, error: 'Sessão não configurada' }
      }

      const sequence = availableSequences.find((item) => item.id === followUpId)

      try {
        setError(null)

        // A RPC concentra as validações (conta, grupo, sequência ativa) e o reset
        // completo do estado, marcando enrollment_source = 'manual' e a âncora de
        // inatividade. É o que faz a cadência disparar mesmo sem assistente ativo.
        const { error: rpcError } = await supabase.rpc('enroll_contact_in_follow_up', {
          p_contact_id: contactId,
          p_follow_up_id: followUpId,
        })

        if (rpcError) throw rpcError

        if (sequence) {
          await logCrmContactInteraction({
            accountId: user.account_id,
            contactId,
            title: 'Entrou na sequência',
            description: `${user.name || 'Usuário'} adicionou o lead à sequência "${sequence.name}" em ${formatCrmInteractionDateTime()}`,
          })
        }

        await fetchSequences()
        broadcastChange(contactId)
        return { ok: true }
      } catch (err: any) {
        console.error('[useContactFollowUpSequences] enroll error:', err)
        const message = err?.message ?? 'Erro ao adicionar o contato à sequência'
        setError(message)
        return { ok: false, error: message }
      }
    },
    [availableSequences, contactId, fetchSequences, user]
  )

  const stop = useCallback(
    async (stateId: string): Promise<FollowUpMutationResult> => {
      if (!supabase || !user?.account_id || !contactId) {
        return { ok: false, error: 'Sessão não configurada' }
      }

      const state = sequences.find((item) => item.id === stateId)

      try {
        setError(null)

        const { error: updateError } = await supabase
          .from('account_contact_follow_up_states')
          .update({
            status: 'stopped',
            stop_reason: 'manual',
            next_fire_at: null,
            next_fire_reason: null,
            dispatch_locked_until: null,
          })
          .eq('id', stateId)

        if (updateError) throw updateError

        await logCrmContactInteraction({
          accountId: user.account_id,
          contactId,
          title: 'Saiu da sequência',
          description: `${user.name || 'Usuário'} encerrou a sequência "${state?.follow_up?.name ?? 'Sequência'}" em ${formatCrmInteractionDateTime()}`,
        })

        await fetchSequences()
        broadcastChange(contactId)
        return { ok: true }
      } catch (err: any) {
        console.error('[useContactFollowUpSequences] stop error:', err)
        const message = err?.message ?? 'Erro ao encerrar a sequência'
        setError(message)
        return { ok: false, error: message }
      }
    },
    [contactId, fetchSequences, sequences, user]
  )

  useEffect(() => {
    if (!supabase || !user?.account_id || !contactId) return

    fetchSequences()

    const channel = supabase
      .channel(`followup-sequences-${contactId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_contact_follow_up_states',
          filter: `account_contact_id=eq.${contactId}`,
        },
        () => {
          // O payload do realtime não traz o join com o nome da sequência.
          void fetchSequences()
        }
      )
      .subscribe()

    return () => {
      supabase?.removeChannel(channel)
    }
  }, [contactId, fetchSequences, user?.account_id])

  useEffect(() => {
    if (!contactId) return
    return subscribeToChanges(contactId, () => {
      void fetchSequences()
    })
  }, [contactId, fetchSequences])

  return {
    sequences,
    availableSequences,
    loading,
    error,
    refetch: fetchSequences,
    enroll,
    stop,
  }
}
